// src/screens/AccountsScreen.js
// DROP-IN REPLACEMENT
//
// ✅ Mimics InventoryScreen layout:
//   - Actions row FIRST
//   - Filters row BELOW actions (handled inside AccountActions)
// ✅ Tabs match ProfileScreen UX/UI (inside rounded card + underline indicator)
// ✅ Tabs: Accounts | Analytics
// ✅ Prevents "blank screen" by (1) keeping both tabs mounted and (2) showing errors on-screen
// ✅ Supports deep-linking /accounts?edit=<accountId> (from the map)
// ✅ NEW: Refresh button in top-right (like InventoryScreen)

import React, { useEffect, useMemo, useState } from "react";
import { getAuth } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";

import AccountActions from "../components/accounts/AccountActions";
import AccountsTable from "../components/accounts/AccountsTable";
import AccountAnalytics from "../components/accounts/AccountAnalytics";
import GridsScreen from "./GridsScreen";

import { Building2, BarChart3, Lock, X, RefreshCw, Database } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import useDeveloperFeatureFlags from "../hooks/useDeveloperFeatureFlags";
import { getFeatureAccessState, mergeFeatureFlags } from "../utils/featureAccess";

// -----------------------------
// Error Boundary (so blank screens become readable errors)
// -----------------------------
class ScreenErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[AccountsScreen] Tab crashed:", error, info);
    this.setState({ info });
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const title = this.props.title || "This section crashed";
    const errMsg = this.state.error?.message || String(this.state.error || "Unknown error");
    const stack = this.state.error?.stack || "";
    const compStack = this.state.info?.componentStack || "";

    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4">
        <div className="text-red-800 font-semibold text-sm">{title}</div>
        <div className="text-red-800 text-[12px] mt-1">{errMsg}</div>

        <div className="mt-3 grid grid-cols-1 gap-2">
          {stack ? (
            <pre className="text-[11px] overflow-auto bg-white border border-red-200 rounded-lg p-2 text-slate-700">
              {stack}
            </pre>
          ) : null}

          {compStack ? (
            <pre className="text-[11px] overflow-auto bg-white border border-red-200 rounded-lg p-2 text-slate-700">
              {compStack}
            </pre>
          ) : null}
        </div>

        <div className="text-[11px] text-red-700 mt-2">
          This is the real error that was causing the blank screen. Fix the first line that references your
          component/file.
        </div>
      </div>
    );
  }
}

export default function AccountsScreen() {
  const auth = getAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const accountColumns = useMemo(
    () => [
      { id: "select", label: "", defaultVisible: true },
      { id: "account", label: "Account", defaultVisible: true },
      { id: "accountNumber", label: "Account #", defaultVisible: false },
      { id: "city", label: "City", defaultVisible: true },
      { id: "territory", label: "Territory", defaultVisible: true },
      { id: "assigned", label: "Assigned", defaultVisible: true },
      { id: "contact", label: "Contact", defaultVisible: true },
      { id: "samples", label: "Samples", defaultVisible: false },
      { id: "portal", label: "Portal", defaultVisible: false },
      { id: "actions", label: "", defaultVisible: true },
    ],
    []
  );

  const [userProfile, setUserProfile] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [companySettings, setCompanySettings] = useState({
    featureFlags: {},
    planType: null,
    subscriptionActive: null,
    stripeSubscriptionId: "",
    seatLimits: null,
  });

  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ territoryId: "all", assignedToId: "all" });
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);
  const [visibleAccounts, setVisibleAccounts] = useState([]);
  const [accountColumnVisibility, setAccountColumnVisibility] = useState({
    select: true,
    account: true,
    accountNumber: false,
    city: true,
    territory: true,
    assigned: true,
    contact: true,
    samples: false,
    portal: false,
    actions: true,
  });
  const [showAccountColumnPicker, setShowAccountColumnPicker] = useState(false);

  const [refreshTick, setRefreshTick] = useState(0);
  const developerFeatureFlags = useDeveloperFeatureFlags();

  // Tabs: "accounts" | "grid" | "analytics"
  const [activeTab, setActiveTab] = useState("accounts");

  // ✅ read edit from URL
  const editFromUrl = (searchParams.get("edit") || "").trim();
  const openEditAccountId = editFromUrl || null;

  const uid = auth.currentUser?.uid || null;

  const role = String(userProfile?.role || "").toLowerCase();
  const isAdminOrOwner = role === "admin" || role === "owner";
  const isEndUser = role === "enduser" || role === "end_user";

  const bumpRefresh = () => setRefreshTick((n) => n + 1);

  useEffect(() => {
    setSelectedAccountIds([]);
  }, [refreshTick, companyId, activeTab]);

  // -----------------------------
  // Load profile
  // -----------------------------
  useEffect(() => {
    if (!uid) {
      setUserProfile(null);
      setCompanyId(null);
      setLoadingProfile(false);
      return;
    }

    setLoadingProfile(true);

    const unsub = onSnapshot(
      doc(db, "users", uid),
      (snap) => {
        const data = snap.exists() ? { id: snap.id, ...snap.data() } : null;
        setUserProfile(data);
        setCompanyId(data?.companyId || data?.distributorId || null);
        setLoadingProfile(false);
      },
      (err) => {
        console.error("[AccountsScreen] Failed loading profile:", err);
        setUserProfile(null);
        setCompanyId(null);
        setLoadingProfile(false);
      }
    );

    return () => unsub();
  }, [uid]);

  useEffect(() => {
    if (!companyId) {
      setCompanySettings({
        featureFlags: {},
        planType: null,
        subscriptionActive: null,
        stripeSubscriptionId: "",
        seatLimits: null,
      });
      return undefined;
    }

    const unsub = onSnapshot(
      doc(db, "companies", companyId),
      (snap) => {
        const data = snap.exists() ? snap.data() || {} : {};
        setCompanySettings({
          featureFlags: data.featureFlags || {},
          planType: data.planType || null,
          subscriptionActive:
            typeof data.subscriptionActive === "boolean" ? data.subscriptionActive : null,
          stripeSubscriptionId: data.stripeSubscriptionId || "",
          seatLimits: data.seatLimits || null,
        });
      },
      (err) => {
        console.warn("[AccountsScreen] Failed loading company settings:", err);
        setCompanySettings({
          featureFlags: {},
          planType: null,
          subscriptionActive: null,
          stripeSubscriptionId: "",
          seatLimits: null,
        });
      }
    );

    return () => unsub();
  }, [companyId]);

  // -----------------------------
  // If URL has ?edit=, force Accounts tab
  // -----------------------------
  useEffect(() => {
    if (openEditAccountId) setActiveTab("accounts");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openEditAccountId]);

  // -----------------------------
  // Helpers to set/clear ?edit=
  // -----------------------------
  const setEditParam = (accountId, { replace = false } = {}) => {
    const next = new URLSearchParams(searchParams);
    if (accountId) next.set("edit", String(accountId));
    else next.delete("edit");
    setSearchParams(next, { replace });
  };

  const clearEditParam = () => setEditParam(null, { replace: true });

  const onOpenEditAccount = (accountId) => {
    if (!accountId) return;
    setEditParam(accountId);
  };

  const onCloseEditAccount = () => {
    clearEditParam();
  };

  const editPillText = useMemo(() => {
    if (!openEditAccountId) return "";
    return `Editing: ${openEditAccountId}`;
  }, [openEditAccountId]);

  const accountAnalyticsAccess = useMemo(
    () =>
      getFeatureAccessState({
        featureKey: "accountAnalytics",
        featureFlags: mergeFeatureFlags(companySettings?.featureFlags || {}, developerFeatureFlags || {}),
        userEmail: userProfile?.email || auth.currentUser?.email || "",
        planType: companySettings?.planType || userProfile?.planType || null,
        subscriptionActive:
          typeof companySettings?.subscriptionActive === "boolean"
            ? companySettings.subscriptionActive
            : userProfile?.subscriptionActive === true,
        stripeSubscriptionId: companySettings?.stripeSubscriptionId || "",
        seatLimits: companySettings?.seatLimits || null,
      }),
    [
      companySettings?.featureFlags,
      companySettings?.planType,
      companySettings?.subscriptionActive,
      companySettings?.stripeSubscriptionId,
      companySettings?.seatLimits,
      developerFeatureFlags,
      userProfile?.email,
      userProfile?.planType,
      userProfile?.subscriptionActive,
      auth.currentUser?.email,
    ]
  );
  const accountAnalyticsHiddenEntirely = accountAnalyticsAccess.reason === "hidden_entirely";

  useEffect(() => {
    if (!accountAnalyticsAccess.allowed && activeTab === "analytics") {
      setActiveTab("accounts");
    }
  }, [accountAnalyticsAccess.allowed, activeTab]);

  useEffect(() => {
    if (typeof window !== "undefined" && window.innerWidth < 768 && activeTab === "grid") {
      setActiveTab("accounts");
      return;
    }
    if (isEndUser && activeTab === "grid") {
      setActiveTab("accounts");
    }
  }, [activeTab, isEndUser]);

  // -----------------------------
  // Guards / loading states
  // -----------------------------
  if (!uid) {
    return (
      <div className="p-6">
        <div className="text-sm text-slate-700">Please sign in to view accounts.</div>
      </div>
    );
  }

  if (loadingProfile) {
    return (
      <div className="p-6">
        <div className="text-sm text-slate-700">Loading profile…</div>
      </div>
    );
  }

  if (!companyId) {
    return (
      <div className="p-6">
        <div className="text-sm text-slate-700">No companyId found on your user profile document.</div>
        <div className="text-xs text-slate-500 mt-2">
          Check <code>/users/{uid}</code> has <code>companyId</code> (or <code>distributorId</code>).
        </div>
      </div>
    );
  }

  const outlineBtn =
    "inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-[13px] font-medium text-slate-700 whitespace-nowrap";
  const outlineBtnDisabled = "disabled:opacity-50 disabled:cursor-not-allowed";

  return (
    <div className="w-full max-w-none mx-0 px-0 py-1">
      <div className="bg-white overflow-hidden md:rounded-2xl md:shadow-lg md:border md:border-slate-200">
        {/* Tabs row – matches InventoryScreen placement + top-right tools */}
        <div className="border-b border-slate-200 px-4 py-3 md:hidden">
          <nav className="inline-flex w-full rounded-2xl border border-slate-200 bg-white p-1 shadow-sm">
            <div className="grid w-full grid-cols-2 gap-1">
              <button
                type="button"
                onClick={() => setActiveTab("accounts")}
                className={`flex items-center justify-center gap-2 rounded-2xl px-3 py-3 text-sm font-medium transition-all ${
                  activeTab === "accounts"
                    ? "bg-blue-50 text-blue-700 shadow-sm"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`}
              >
                <Building2 className="w-4 h-4 shrink-0" />
                <span>Accounts</span>
              </button>
              {accountAnalyticsHiddenEntirely ? null : (
                <button
                  type="button"
                  onClick={() => accountAnalyticsAccess.allowed && setActiveTab("analytics")}
                  title={!accountAnalyticsAccess.allowed ? (accountAnalyticsAccess.lockMessage || "Analytics locked") : ""}
                  className={`flex items-center justify-center gap-2 rounded-2xl px-3 py-3 text-sm font-medium transition-all ${
                    !accountAnalyticsAccess.allowed
                      ? "text-slate-400 bg-slate-50 cursor-not-allowed"
                      : activeTab === "analytics"
                        ? "bg-blue-50 text-blue-700 shadow-sm"
                        : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                >
                  <BarChart3 className="w-4 h-4 shrink-0" />
                  <span>Analytics</span>
                  {!accountAnalyticsAccess.allowed ? <Lock className="w-3.5 h-3.5 shrink-0 opacity-70" /> : null}
                </button>
              )}
            </div>
          </nav>
        </div>

        <div className="hidden md:block border-b border-slate-200">
          <div className="hidden md:flex flex-col gap-3 px-4 py-4 md:px-6 md:py-0 lg:flex-row lg:items-stretch lg:gap-0">
            <div className="min-w-0 pt-0 md:pt-4 lg:hidden">
              <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">
                Accounts
              </div>
              <div className="mt-1 text-sm text-slate-500">
                {isAdminOrOwner ? "Admin view" : "Assigned accounts view"}
              </div>
            </div>

            <nav className="flex text-sm overflow-x-auto">
              <button
                type="button"
                onClick={() => setActiveTab("accounts")}
                className={`flex items-center gap-2 px-6 py-4 font-medium transition-all relative ${
                  activeTab === "accounts"
                    ? "text-blue-600 bg-blue-50"
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                }`}
              >
                <Building2 className="w-5 h-5" />
                <span>Accounts</span>
                {activeTab === "accounts" && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />}
              </button>

              <button
                type="button"
                onClick={() => !isEndUser && setActiveTab("grid")}
                disabled={isEndUser}
                title={isEndUser ? "Grid is unavailable for end users" : ""}
                className={`flex items-center gap-2 px-6 py-4 font-medium transition-all relative ${
                  isEndUser
                    ? "text-slate-300 bg-slate-50 cursor-not-allowed"
                    : activeTab === "grid"
                    ? "text-blue-600 bg-blue-50"
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                }`}
              >
                <Database className="w-5 h-5" />
                <span>Grid</span>
                {isEndUser ? <Lock className="w-3.5 h-3.5 opacity-70" /> : null}
                {activeTab === "grid" && !isEndUser && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />}
              </button>

              {accountAnalyticsHiddenEntirely ? null : (
                <button
                  type="button"
                  onClick={() => accountAnalyticsAccess.allowed && setActiveTab("analytics")}
                  title={!accountAnalyticsAccess.allowed ? (accountAnalyticsAccess.lockMessage || "Analytics locked") : ""}
                  className={`flex items-center gap-2 px-6 py-4 font-medium transition-all relative ${
                    !accountAnalyticsAccess.allowed
                      ? "text-slate-400 bg-slate-50 cursor-not-allowed"
                      : activeTab === "analytics"
                        ? "text-blue-600 bg-blue-50"
                        : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                  }`}
                >
                  <BarChart3 className="w-5 h-5" />
                  <span>Analytics</span>
                  {!accountAnalyticsAccess.allowed ? <Lock className="w-3.5 h-3.5 opacity-70" /> : null}
                  {activeTab === "analytics" && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />}
                </button>
              )}
            </nav>

            {/* Top-right: deep-link pill + view label + refresh */}
            <div className="flex items-center justify-between gap-2 lg:ml-auto lg:px-4">
              {openEditAccountId ? (
                <div className="inline-flex items-center gap-2 px-2 py-1 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-[11px]">
                  <span className="truncate max-w-[220px]">{editPillText}</span>
                  <button
                    type="button"
                    onClick={clearEditParam}
                    className="p-0.5 rounded hover:bg-blue-100"
                    title="Close edit link"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : null}

              <div className="hidden text-[11px] text-slate-500 whitespace-nowrap lg:block">
                {isAdminOrOwner ? "Admin view" : "Assigned accounts view"}
              </div>

              <button
                type="button"
                onClick={bumpRefresh}
                className={`${outlineBtn} ${outlineBtnDisabled}`}
                title="Refresh accounts"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-3 md:p-6">
          {/* Keep both tabs mounted (prevents remount bugs / blank screens) */}
          <div className={activeTab === "accounts" ? "block" : "hidden"}>
            <ScreenErrorBoundary title="Accounts tab crashed (this was causing the blank screen)">
              <div className="min-h-[50vh]">
              <AccountActions
  companyId={companyId}
  userProfile={userProfile}
  visibleAccounts={visibleAccounts}
  accountColumns={accountColumns}
  columnVisibility={accountColumnVisibility}
  onColumnVisibilityChange={setAccountColumnVisibility}
  showColumnPicker={showAccountColumnPicker}
  setShowColumnPicker={setShowAccountColumnPicker}
  search={search}
  onSearchChange={setSearch}
  filters={filters}
  onFiltersChange={setFilters}
  selectedAccountIds={selectedAccountIds}
  onClearSelectedAccounts={() => setSelectedAccountIds([])}

  // ✅ allow create for endUser/admin/owner
  canCreate={true}

  // ✅ import stays admin/owner only
  canImport={isAdminOrOwner}

  // ✅ refresh after import/create
  onAfterImport={bumpRefresh}
/>


                <div className="mt-3 md:mt-3">
                  <AccountsTable
                    companyId={companyId}
                    userProfile={userProfile}
                    search={search}
                    filters={filters}
                    accountColumns={accountColumns}
                    columnVisibility={accountColumnVisibility}
                    refreshKey={refreshTick}
                    onChanged={bumpRefresh}
                    selectedAccountIds={selectedAccountIds}
                    onSelectedAccountIdsChange={setSelectedAccountIds}
                    onVisibleAccountsChange={setVisibleAccounts}
                    openEditAccountId={openEditAccountId}
                    onOpenEditAccount={onOpenEditAccount}
                    onCloseEditAccount={onCloseEditAccount}
                  />
                </div>
              </div>
            </ScreenErrorBoundary>
          </div>

          <div className={activeTab === "analytics" && accountAnalyticsAccess.allowed ? "block" : "hidden"}>
            <ScreenErrorBoundary title="Analytics tab crashed">
              <AccountAnalytics
                companyId={companyId}
                refreshKey={refreshTick}
                currentUserId={uid}
                userProfile={userProfile}
              />
            </ScreenErrorBoundary>
          </div>

          <div className={activeTab === "grid" && !isEndUser ? "block" : "hidden"}>
            <ScreenErrorBoundary title="Accounts Grid tab crashed">
              <div className="min-h-[50vh]">
                <div className="text-xs text-slate-500 mb-3">
                  Spreadsheet-style account editing, embedded here instead of living as a separate section.
                </div>
                <GridsScreen forcedTab="accounts" showTabNav={false} embedded />
              </div>
            </ScreenErrorBoundary>
          </div>
        </div>
      </div>
    </div>
  );
}
